// ./interceptor
/**
 * 1. Axios catch block vs interceptor
 * If using Axios, a Network Error (like when the API is down) is thrown before a response is received — so it never hits response interceptors. We need to handle it in the error interceptor or in your .catch() block.
 *
 * 2. Global error boundary (React) only catches render-time errors
 * React’s ErrorBoundary only catches errors during rendering, lifecycle methods, or constructors — not async errors like failed API calls.
 *
 * 3. Silent failure in your API wrapper
 * If you have a wrapper like api.get(...), make sure it doesn’t swallow errors silently or rethrow without triggering UI feedback.
 *
 */
import { notifications } from "@mantine/notifications";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_NETWORK_ERROR,
  MSG_DEFAULT_NETWORK_ERROR_TITLE,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { safeLog } from "common/utils";
import { authApi, publicApi } from "./";
import { logError } from "./diagnostic.service";

const handleError = (error: any) => {
  const status = error?.response?.status;
  const method = error?.config?.method?.toLowerCase?.() ?? "get";

  if (error.message === "Network Error") {
    notifications.show({
      title: MSG_DEFAULT_NETWORK_ERROR_TITLE,
      message: MSG_DEFAULT_NETWORK_ERROR,
      color: MantineNotificationColor.Error,
      position: MantineNotificationPosition.TopCenter,
    });
    navigateTo("/application-error");
    return Promise.reject(error);
  }

  try {
    logError(error);
  } catch (err) {
    safeLog("Unhandled API Call: ", err);
  }

  // 🚫 Never redirect on GET requests
  if (method === "get") {
    return Promise.reject(error);
  }

  // 🔐 401 = not authenticated → go to login
  if (status === 401) {
    navigateTo("/login");
    return Promise.reject(error);
  }

  // 🚫 403 = authenticated but forbidden → go to not-authorized
  if (status === 403) {
    navigateTo("/not-authorized");
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

// Attach to both APIs
authApi.interceptors.response.use((res) => res, handleError);
publicApi.interceptors.response.use((res) => res, handleError);
